import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';

import {add, deleteAllRecords, deleteRecord, fetch, fetchRecords, update,} from 'services/AccountsService';

import {fetchRecords as fetchUserRecords, fetchUserWithLocation,} from 'services/UsersService';

import {
    deleteAllCase,
    deleteOneCase,
    fetchManyCase,
    fetchOneCase,
    saveOneCase,
    updateOneCase,
} from 'store/RecordsSliceCaseHelpers';

import {schema} from 'normalizr';

const entityKey = 'accounts';

export const entitySchema = new schema.Entity(entityKey);
export const arrayOfRecords = new schema.Array(entitySchema);

const LOCAL_STORAGE_KEY = 'pac-accounts';
const DEFAULT_LOCAL_STORAGE_VALUE = {};

const initialState = {
    byId: {},
    ids: [],
    totalCount: 0,
    loading: false,
    error: '',
    signOutResult: null,
};

const convertRecord = (record, emailAddress) => {
    let nextCloudIdentityProvider = null;

    if (
        typeof record['federated-id-providers'] !== 'undefined' &&
        Array.isArray(record['federated-id-providers']) &&
        record['federated-id-providers'].length > 0
    ) {
        nextCloudIdentityProvider = record['federated-id-providers'][0];
    }

    return Object.assign(
        {},
        {
            id: emailAddress.toLowerCase(),
            username: record['username'],
            cognitoUserPoolDomain: record['cognito-user-pool-domain'],
            appClientId: record['app-client-id'],
            description: record['description'],
            callbackUrls: record['callback-urls'],
            icaoCode: record['icao-code'],
            cognitoUserPoolId: record['cognito-user-pool-id'],
            federatedIdProviders: record['federated-id-providers'],
            firstName: record['first-name'],
            lastName: record['last-name'],
            emailAddress: emailAddress,
            nextcloudIdentityProvider: nextCloudIdentityProvider,
            isAuthorizing: 'no',
            isVisible: 'yes',
        }
    );
};

const normalizeRecord = (record) => {
    return Object.assign({}, record, {
        id: record.id,
    });
};

// genenrates pending, fulfilled and rejected
export const fetchAccounts = createAsyncThunk(
    `${entityKey}/fetchRecords`,
    (query) => {
        return fetchRecords(query).then((response) => {
            return response.map((record) => {
                return normalizeRecord(record);
            });
        });
    }
);

export const fetchAccount = createAsyncThunk(`${entityKey}/fetch`, (id) => {
    return fetch(id).then((response) => {
        return normalizeRecord(response);
    });
});

export const saveAccount = createAsyncThunk(`${entityKey}/add`, (payload) => {
    return fetchRecords(
        (c) =>
            c.emailAddress('eq', payload.emailAddress) &&
            c.username('eq', payload.username)
    ).then((response) => {
        if (Array.isArray(response) && response.length > 0) {
            return fetch(response[0].id);
        }

        return add(payload).then((response) => response);
    });
});

export const updateAccount = createAsyncThunk(
    `${entityKey}/update`,
    ({ id, record }) => {
        return update(id, record).then((response) => record);
    }
);

export const deleteAccount = createAsyncThunk(
    `${entityKey}/deleteRecord`,
    (id) => {
        return deleteRecord(id).then((response) => id);
    }
);

export const deleteAllAccounts = createAsyncThunk(
    `${entityKey}/deleteAllRecords`,
    () => {
        return deleteAllRecords().then((response) => response);
    }
);

export const searchAccounts = createAsyncThunk(
    `${entityKey}/search`,
    async (emailAddress) => {
        return fetchRecords((c) => c.emailAddress('eq', emailAddress)).then(
            async (response) => {
                if (Array.isArray(response) && response.length > 0) {
                    const record = response[0];
                    const updated = Object.assign({}, record, {
                        isVisible: 'yes',
                    });
                    return update(record.id, updated).then(
                        (response) => updated
                    );
                }

                const location = await fetchUserLocation(emailAddress);
                console.debug('IN SEARCH ACCOUNT', location);

                const user = await fetchUserWithLocation(location);
                const payload = convertRecord(user, emailAddress);
                return add(payload).then((response) => response);
            }
        );
    }
);

const fetchUserLocation = async (emailAddress) => {
    const location = await fetchUserRecords({
        email_address: emailAddress,
    }).then(
        (response) => {
            console.debug('OK RESPONSE', response);
        },
        (error) => {
            console.debug('IN FETCH USER LOCATION', error);

            const { data, status } = error.response;

            // received Location header
            if (
                status === 302 &&
                typeof data['location'] !== 'undefined' &&
                data['location'] !== ''
            ) {
                return data['location'];
            }
        }
    );

    return location;
};

const accountsSlice = createSlice({
    name: entityKey,
    initialState,
    extraReducers: (builder) => {
        // FETCH MANY
        fetchManyCase(builder, fetchAccounts, entityKey);

        // FETCH ONE
        fetchOneCase(builder, fetchAccount);

        // SAVE ONE
        saveOneCase(builder, saveAccount);

        // UPDATE ONE
        updateOneCase(builder, updateAccount);

        // DELETE ONE
        deleteOneCase(builder, deleteAccount);

        // DELETE ALL
        deleteAllCase(builder, deleteAllAccounts);

        // SEARCH WITH EMAIL
        saveOneCase(builder, searchAccounts);
    },
});

export default accountsSlice.reducer;
